import { BLOCKS } from '@contentful/rich-text-types'
import {
  RenderRichTextData,
  renderRichText,
} from 'gatsby-source-contentful/rich-text'
import React, { ReactNode, useContext } from 'react'

import { LocaleContext } from '../../locale/i18n'
import { toDashedSlug } from '../../logic/formatting'
import {
  RichTextAssetReferences,
  RichTextEntryLinkReference,
  RichTextEntryReference,
} from '../../logic/richText'

import './TableOfContents.scss'

const getHeaders = (
  text: RenderRichTextData<
    | RichTextAssetReferences
    | RichTextEntryLinkReference
    | RichTextEntryReference
  >,
) => {
  const headers: {
    header: ReactNode
    level: string | null
    id: string
  }[] = []
  const hashes: string[] = []

  renderRichText(text, {
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: (node, _) => {
        if (node.data.target === undefined) {
          return null
        }

        const target = node.data.target as RichTextEntryReference
        const hash =
          target.heading &&
          '#' + toDashedSlug(target.tableOfContentsHeading ?? target.heading)

        if (hash) {
          headers.push({
            header: <a href={hash}>{target.tableOfContentsHeading}</a>,
            id: target.contentful_id,
            level: target.level,
          })
          hashes.push(hash)
        }
        return null
      },
    },
  })

  const duplicateHashes = hashes.filter((h, i, a) => a.indexOf(h) !== i)

  if (duplicateHashes.length > 0) {
    throw new Error(
      `Duplicate header with hash ${duplicateHashes.join(
        ', ',
      )}. Cannot render table of contents.`,
    )
  }

  return headers
}

export const TableOfContents = ({
  section,
}: {
  section: RenderRichTextData<
    | RichTextAssetReferences
    | RichTextEntryLinkReference
    | RichTextEntryReference
  >
}) => {
  const { t } = useContext(LocaleContext)

  const headers = section ? getHeaders(section) : [].flat()

  return (
    <div className="tableOfContents">
      <h3 className="tableOfContents__title">
        {t('blogPostPage.tableOfContents')}
      </h3>
      <ul className="tableOfContents__list">
        {headers.map(h => {
          if (h.level === 'h3') {
            return (
              <ul key={h.id} className="tableOfContents__list">
                <li className="tableOfContents__listItem">{h.header}</li>
              </ul>
            )
          }
          if (h.level === 'h2') {
            return (
              <li key={h.id} className="tableOfContents__listItem">
                {h.header}
              </li>
            )
          }
          return null
        })}
      </ul>
    </div>
  )
}
